<script lang="ts">
	import { afterNavigate } from '$app/navigation';
	import Button from '$lib/Button.svelte';
	import { schema } from '$lib/schemas/comment';
	import mixpanel from 'mixpanel-browser';
	import type { Infer, SuperValidated } from 'sveltekit-superforms';
	import { superForm } from 'sveltekit-superforms/client';
	import { addToast } from './Toaster.svelte';

	interface Props {
		formData: SuperValidated<Infer<typeof schema>>;
		documentId: string;
		replyTo?: BaseComment | null;
		type: 'question' | 'comment' | 'discussion';
	}

	let { formData, documentId, replyTo = $bindable(null), type = 'comment' }: Props = $props();

	const { form, message, errors, constraints, submitting } = superForm(formData, {
		multipleSubmits: 'prevent'
	});

	$effect(() => {
		$form.threadOf = replyTo?.id;
	});

	afterNavigate(() => {
		$form.id = mixpanel.get_distinct_id() as string;
		$form.documentId = documentId;

		if ($message) {
			addToast({
				data: {
					status: $message.status,
					message: $message.text
				}
			});
		}
	});

	let placeholder = $derived(
		type === 'comment'
			? 'What are your thoughts?'
			: type === 'question'
				? 'Plant your question here, and we’ll help it grow'
				: 'What’s your take on this stock?'
	);
</script>

<form method="POST" class="grid">
	<input type="hidden" name="id" bind:value={$form.id} />
	<input type="hidden" name="documentId" bind:value={$form.documentId} />
	<input type="hidden" name="threadOf" bind:value={$form.threadOf} />

	<div class="form-control">
		<label for="content">
			{#if replyTo}
				Replying to {replyTo.isAdminComment ? 'Beansprout' : replyTo.author.name}
			{:else if type === 'question'}
				Ask a question
			{:else if type === 'comment'}
				Leave a comment
			{:else}
				Join the discussion
			{/if}
		</label>
		<textarea
			name="content"
			{placeholder}
			rows="4"
			bind:value={$form.content}
			{...$constraints.content}
		></textarea>
		{#if $errors.content}
			<p class="text-red-ticker">
				{$errors.content}
			</p>
		{/if}
	</div>

	<div class="form-control">
		<label for="name">Name*</label>
		<input
			type="text"
			name="name"
			placeholder="Name (Required)"
			bind:value={$form.name}
			{...$constraints.name}
		/>
		{#if $errors.name}
			<p class="text-red-ticker">
				{$errors.name}
			</p>
		{/if}
	</div>

	{#if replyTo}
		<div class="mx-auto flex">
			<button onclick={() => (replyTo = null)} class="card-bold text-gray-mid">Cancel</button>
			<Button type="submit" class="ml-5 w-40">
				{type === 'question' ? 'Share an answer' : 'Reply'}
			</Button>
		</div>
	{:else}
		<div class="mx-auto">
			<Button type="submit" disabled={$submitting} variant="primary">
				Post {type === 'question' ? 'question' : 'comment'}
			</Button>
		</div>
	{/if}
</form>

<style lang="postcss">
	@reference '../app.css';

	.form-control {
		@apply mb-4;
	}

	form {
		@apply section-mb;

		label {
			@apply text-gray-dark mb-1 block;
		}

		input {
			@apply border-gray-light block w-full rounded-2xl border-1 p-5;
		}

		textarea {
			@apply border-gray-light w-full resize-none rounded-2xl border-1 p-5;
		}
	}
</style>
